import React, { useEffect, useRef, useState } from 'react';

/**
 * Campo numérico controlado que SÍ se puede vaciar.
 *
 * Por qué (29-oct-2026, Dave / Bataker, plan gratis): los `<input type="number" value={n}
 * onChange={e => setN(Number(e.target.value))}>` convierten el texto en número en cada tecla.
 * Al borrar todo, `Number('')` es 0, React vuelve a pintar «0» y en el celular ya no hay forma de
 * quitarlo: para poner 1 % de cashback el usuario se quedaba con «50», «05», etc. Aquí el texto
 * vive en el campo mientras se escribe y el padre recibe el número (vacío → `emptyAs`, 0 por
 * defecto). Si el padre cambia el valor por fuera (preset, reset), el texto se vuelve a sincronizar.
 */
type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: number | null | undefined;
  onValue: (n: number) => void;
  /** Qué número recibe el padre cuando el campo queda vacío o ilegible. */
  emptyAs?: number;
};

const fmt = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '' : String(v));

export default function NumInput({ value, onValue, emptyAs = 0, ...rest }: Props) {
  const [txt, setTxt] = useState(fmt(value));
  const emitted = useRef<number | null | undefined>(value);

  useEffect(() => {
    // Solo cambios que NO salieron de este campo (si el padre guarda lo que emitimos, el texto se queda tal cual).
    if (value !== emitted.current && !(Number.isNaN(value as number) && Number.isNaN(emitted.current as number))) {
      setTxt(fmt(value));
      emitted.current = value;
    }
  }, [value]);

  return (
    <input
      type="number"
      {...rest}
      value={txt}
      onChange={(e) => {
        const s = e.target.value;
        setTxt(s);
        const n = s.trim() === '' ? emptyAs : Number(s);
        const out = Number.isNaN(n) ? emptyAs : n;
        emitted.current = out;
        onValue(out);
      }}
    />
  );
}
